import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Calculator,
  ChevronDown,
  ChevronUp,
  Download,
  Loader2,
  Home,
  Thermometer,
  Box,
  Zap,
  Settings,
  BarChart3,
  ArrowLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULTS = {
  ref_type: "A-Type -40°C",
  length_mm: "",
  width_mm: "",
  height_mm: "",
  ramp_K_per_min: "",
  product_mass_kg: "",
  product_live_W: "0",
  T_inside_C: "-40",
  T_ambient_C: "45",
  T_initial_C: "180",
  U_Wm2K: "0.5",
  panel_thickness_m: "0.0012",
  metal_density_kg_m3: "8000",
  Cp_metal_kcal_per_kgK: "0.11",
  partition_mass_kg_m2: "23",
  product_eff: "1.0",
  Cp_product_kcal_per_kgK: "0.12",
  motor_hp: "0.5",
  motor_qty: "1",
  motor_heat_fraction: "0.30",
  imp_qty: "1",
  imp_kg_each: "1.0",
  imp_cp_kcal_per_kgK: "0.21",
  safety_pct: "0",
};

const InputField = ({ label, name, value, onChange, unit, placeholder, type = "number", step }) => (
  <div>
    <label className="block text-xs text-gray-400 mb-1 font-mono">{label}</label>
    <div className="relative">
      <input
        data-testid={`hl-input-${name}`}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        step={step}
        className="w-full bg-white/5 border border-white/10 text-gray-100 px-3 py-2 text-sm font-mono
          focus:outline-none focus:border-blue-500/50 transition-colors placeholder-gray-600"
      />
      {unit && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">{unit}</span>
      )}
    </div>
  </div>
);

const ResultCard = ({ label, value, unit, accent }) => (
  <div className={`bg-white/5 border ${accent ? 'border-blue-500/30' : 'border-white/10'} p-4`}>
    <div className="text-xs text-gray-400 font-mono mb-1">{label}</div>
    <div className={`text-2xl font-mono font-bold ${accent ? 'text-blue-400' : 'text-gray-100'}`}>
      {value}
    </div>
    {unit && <div className="text-xs text-gray-500 mt-1">{unit}</div>}
  </div>
);

const KitCard = ({ kit, index }) => {
  const adequate = kit.is_adequate;
  const recommended = kit.is_recommended;
  return (
    <div
      data-testid={`kit-recommendation-${index}`}
      className={`border p-4 ${recommended ? 'border-emerald-500/50 bg-emerald-500/5' : adequate ? 'border-blue-500/30 bg-blue-500/5' : 'border-amber-500/30 bg-amber-500/5'}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-mono text-gray-400">Pick #{index + 1}</span>
        {recommended && (
          <span className="text-xs font-mono px-2 py-0.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
            RECOMMENDED
          </span>
        )}
      </div>
      <div className="text-xl font-mono font-bold text-gray-100">{kit.model}</div>
      {kit.cap_kW != null && (
        <>
          <div className="text-sm text-gray-400 mt-1">{kit.cap_kW} kW capacity</div>
          <div className={`text-xs mt-2 font-mono ${adequate ? 'text-emerald-400' : 'text-amber-400'}`}>
            {adequate ? 'Adequate' : 'Undersized'} ({kit.delta_kW != null ? `Δ ${kit.delta_kW} kW` : '—'})
          </div>
        </>
      )}
    </div>
  );
};

const BreakdownBar = ({ components, totalKW }) => {
  if (!components || components.length === 0) return null;
  return (
    <div>
      <div className="flex h-8 overflow-hidden border border-white/10" data-testid="breakdown-bar">
        {components.map((c, i) => {
          const pct = totalKW > 0 ? (c.value_kW / totalKW) * 100 : 0;
          if (pct < 0.5) return null;
          return (
            <div
              key={i}
              title={`${c.name}: ${c.value_kW.toFixed(3)} kW (${pct.toFixed(1)}%)`}
              style={{ width: `${pct}%`, backgroundColor: c.color }}
              className="transition-all duration-300"
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3 mt-3">
        {components.map((c, i) => {
          const pct = totalKW > 0 ? (c.value_kW / totalKW) * 100 : 0;
          return (
            <div key={i} className="flex items-center gap-1.5 text-xs text-gray-400">
              <div className="w-2.5 h-2.5" style={{ backgroundColor: c.color }} />
              <span className="font-mono">{c.name}</span>
              <span className="text-gray-600">({pct.toFixed(1)}%)</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function HeatLoadCalculator({ user }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(DEFAULTS);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleChange = useCallback((e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);

  const handleCalculate = async () => {
    if (!form.length_mm || !form.width_mm || !form.height_mm || !form.ramp_K_per_min || !form.product_mass_kg) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (k === "ref_type") {
          payload[k] = v;
        } else if (k === "motor_qty" || k === "imp_qty") {
          payload[k] = parseInt(v) || 0;
        } else {
          payload[k] = parseFloat(v) || 0;
        }
      }
      const resp = await axios.post(`${API}/heat-load/calculate`, payload);
      setResult(resp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Calculation failed");
    }
    setLoading(false);
  };

  const handleExportPDF = async () => {
    if (!result) return;
    setExporting(true);
    try {
      const payload = {
        calculation: result,
        inputs: {},
      };
      for (const [k, v] of Object.entries(form)) {
        if (k === "ref_type") {
          payload.inputs[k] = v;
        } else if (k === "motor_qty" || k === "imp_qty") {
          payload.inputs[k] = parseInt(v) || 0;
        } else {
          payload.inputs[k] = parseFloat(v) || 0;
        }
      }
      const resp = await axios.post(`${API}/heat-load/export-pdf`, payload, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement("a");
      link.href = url;
      const cd = resp.headers["content-disposition"];
      const filename = cd ? cd.split("filename=")[1] : "CME_HeatLoad_Report.pdf";
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error("PDF export failed");
    }
    setExporting(false);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {}
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen" style={{ background: "#0d1520" }}>
      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 z-30" style={{ background: "#0d1520" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/")}
              className="text-gray-400 hover:text-white transition-colors"
              data-testid="hl-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-emerald-400" />
              <h1 className="text-lg font-mono font-semibold text-white">
                Heat Load Calculator
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user?.picture && (
              <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
            )}
            <span className="text-xs text-gray-400 hidden sm:inline">{user?.name || user?.email}</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid lg:grid-cols-5 gap-6">
          {/* LEFT: Inputs */}
          <div className="lg:col-span-2 space-y-5">
            {/* Refrigeration Type */}
            <section className="bg-white/[0.03] border border-white/10 p-5">
              <div className="flex items-center gap-2 mb-4">
                <Thermometer className="w-4 h-4 text-blue-400" />
                <h2 className="text-sm font-mono font-semibold text-gray-200 uppercase tracking-wider">
                  Refrigeration Type
                </h2>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {["A-Type -40°C", "B-Type -70°C"].map((t) => (
                  <button
                    key={t}
                    data-testid={`hl-ref-type-${t.split(" ")[0]}`}
                    onClick={() => setForm((p) => ({ ...p, ref_type: t }))}
                    className={`py-3 px-4 text-sm font-mono border transition-all
                      ${form.ref_type === t
                        ? "border-blue-500 bg-blue-500/10 text-blue-400"
                        : "border-white/10 text-gray-400 hover:border-white/20"
                      }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </section>

            {/* Chamber Dimensions */}
            <section className="bg-white/[0.03] border border-white/10 p-5">
              <div className="flex items-center gap-2 mb-4">
                <Box className="w-4 h-4 text-emerald-400" />
                <h2 className="text-sm font-mono font-semibold text-gray-200 uppercase tracking-wider">
                  Chamber Dimensions
                </h2>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <InputField label="Length" name="length_mm" value={form.length_mm} onChange={handleChange} unit="mm" placeholder="1000" />
                <InputField label="Width" name="width_mm" value={form.width_mm} onChange={handleChange} unit="mm" placeholder="1000" />
                <InputField label="Height" name="height_mm" value={form.height_mm} onChange={handleChange} unit="mm" placeholder="1000" />
              </div>
              {form.length_mm && form.width_mm && form.height_mm && (
                <div className="mt-3 text-xs text-gray-500 font-mono">
                  Volume: {((parseFloat(form.length_mm) * parseFloat(form.width_mm) * parseFloat(form.height_mm)) / 1e6).toFixed(2)} L
                </div>
              )}
            </section>

            {/* Product & Ramp */}
            <section className="bg-white/[0.03] border border-white/10 p-5">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-4 h-4 text-amber-400" />
                <h2 className="text-sm font-mono font-semibold text-gray-200 uppercase tracking-wider">
                  Product & Ramp
                </h2>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <InputField label="Ramp Rate" name="ramp_K_per_min" value={form.ramp_K_per_min} onChange={handleChange} unit="K/min" placeholder="3" step="0.1" />
                <InputField label="Product Mass" name="product_mass_kg" value={form.product_mass_kg} onChange={handleChange} unit="kg" placeholder="50" />
                <InputField label="Product Live Load" name="product_live_W" value={form.product_live_W} onChange={handleChange} unit="W" placeholder="0" />
                <InputField label="Safety Margin" name="safety_pct" value={form.safety_pct} onChange={handleChange} unit="%" placeholder="0" />
              </div>
            </section>

            {/* Advanced Parameters */}
            <section className="bg-white/[0.03] border border-white/10">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full flex items-center justify-between p-5 text-sm text-gray-400 hover:text-gray-200 transition-colors"
                data-testid="hl-advanced-toggle"
              >
                <div className="flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  <span className="font-mono uppercase tracking-wider text-xs">Advanced Parameters</span>
                </div>
                {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showAdvanced && (
                <div className="px-5 pb-5 space-y-4">
                  <div className="text-xs text-gray-500 font-mono mb-2">TEMPERATURES</div>
                  <div className="grid grid-cols-3 gap-3">
                    <InputField label="Inside Temp" name="T_inside_C" value={form.T_inside_C} onChange={handleChange} unit="°C" />
                    <InputField label="Ambient Temp" name="T_ambient_C" value={form.T_ambient_C} onChange={handleChange} unit="°C" />
                    <InputField label="Initial Temp" name="T_initial_C" value={form.T_initial_C} onChange={handleChange} unit="°C" />
                  </div>
                  <div className="text-xs text-gray-500 font-mono mb-2 mt-4">PANEL & INSULATION</div>
                  <div className="grid grid-cols-2 gap-3">
                    <InputField label="U-value" name="U_Wm2K" value={form.U_Wm2K} onChange={handleChange} unit="W/m²K" step="0.01" />
                    <InputField label="Panel Thickness" name="panel_thickness_m" value={form.panel_thickness_m} onChange={handleChange} unit="m" step="0.0001" />
                    <InputField label="Metal Density" name="metal_density_kg_m3" value={form.metal_density_kg_m3} onChange={handleChange} unit="kg/m\u00b3" />
                    <InputField label="Cp Metal" name="Cp_metal_kcal_per_kgK" value={form.Cp_metal_kcal_per_kgK} onChange={handleChange} unit="kcal/kgK" step="0.01" />
                    <InputField label="Partition Mass" name="partition_mass_kg_m2" value={form.partition_mass_kg_m2} onChange={handleChange} unit="kg/m²" />
                  </div>
                  <div className="text-xs text-gray-500 font-mono mb-2 mt-4">PRODUCT</div>
                  <div className="grid grid-cols-2 gap-3">
                    <InputField label="Product Efficiency" name="product_eff" value={form.product_eff} onChange={handleChange} step="0.1" />
                    <InputField label="Cp Product" name="Cp_product_kcal_per_kgK" value={form.Cp_product_kcal_per_kgK} onChange={handleChange} unit="kcal/kgK" step="0.01" />
                  </div>
                  <div className="text-xs text-gray-500 font-mono mb-2 mt-4">MOTORS & IMPELLERS</div>
                  <div className="grid grid-cols-2 gap-3">
                    <InputField label="Motor HP" name="motor_hp" value={form.motor_hp} onChange={handleChange} unit="HP" step="0.1" />
                    <InputField label="Motor Qty" name="motor_qty" value={form.motor_qty} onChange={handleChange} />
                    <InputField label="Heat Fraction" name="motor_heat_fraction" value={form.motor_heat_fraction} onChange={handleChange} step="0.01" />
                    <InputField label="Impeller Qty" name="imp_qty" value={form.imp_qty} onChange={handleChange} />
                    <InputField label="Imp. Mass Each" name="imp_kg_each" value={form.imp_kg_each} onChange={handleChange} unit="kg" step="0.1" />
                    <InputField label="Imp. Cp" name="imp_cp_kcal_per_kgK" value={form.imp_cp_kcal_per_kgK} onChange={handleChange} unit="kcal/kgK" step="0.01" />
                  </div>
                </div>
              )}
            </section>

            {/* Calculate Button */}
            <button
              onClick={handleCalculate}
              disabled={loading}
              className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white font-mono text-sm
                uppercase tracking-wider transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="hl-calculate-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
              {loading ? "Calculating..." : "Calculate Heat Load"}
            </button>
          </div>

          {/* RIGHT: Results */}
          <div className="lg:col-span-3 space-y-5">
            {!result ? (
              <div className="bg-white/[0.03] border border-white/10 p-12 flex flex-col items-center justify-center text-center min-h-[400px]">
                <BarChart3 className="w-12 h-12 text-gray-700 mb-4" />
                <h3 className="text-lg font-mono text-gray-500 mb-2">No Results Yet</h3>
                <p className="text-sm text-gray-600 max-w-sm">
                  Enter chamber dimensions, product details, and ramp rate, then click Calculate to see results.
                </p>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-3" data-testid="hl-results-summary">
                  <ResultCard label="Total Load" value={result.total_kW.toFixed(3)} unit="kW" accent />
                  <ResultCard label="BTU/hr" value={Math.round(result.total_BTUh).toLocaleString()} />
                  <ResultCard label="kcal/hr" value={Math.round(result.total_kcalh).toLocaleString()} />
                </div>

                {/* Metadata */}
                <div className="grid grid-cols-3 gap-3">
                  <ResultCard label="Surface Area" value={result.surface_area_m2} unit="m²" />
                  <ResultCard label="ΔT" value={result.delta_T} unit="°C" />
                  <ResultCard label="Metal Mass" value={result.metal_mass_kg} unit="kg" />
                </div>

                {/* Breakdown */}
                <section className="bg-white/[0.03] border border-white/10 p-5">
                  <h2 className="text-sm font-mono font-semibold text-gray-200 uppercase tracking-wider mb-4">
                    Heat Load Breakdown
                  </h2>
                  <BreakdownBar components={result.components} totalKW={result.total_kW} />

                  {/* Component Table */}
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="hl-breakdown-table">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-2 text-xs font-mono text-gray-500 uppercase">Component</th>
                          <th className="text-right py-2 text-xs font-mono text-gray-500 uppercase">Load (kW)</th>
                          <th className="text-right py-2 text-xs font-mono text-gray-500 uppercase">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.components.map((c, i) => (
                          <tr key={i} className="border-b border-white/5">
                            <td className="py-2 text-gray-300 font-mono flex items-center gap-2">
                              <div className="w-2 h-2" style={{ backgroundColor: c.color }} />
                              {c.name}
                            </td>
                            <td className="py-2 text-right text-gray-200 font-mono">{c.value_kW.toFixed(3)}</td>
                            <td className="py-2 text-right text-gray-400 font-mono">
                              {result.total_kW > 0 ? ((c.value_kW / result.total_kW) * 100).toFixed(1) : 0}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Kit Recommendations */}
                <section className="bg-white/[0.03] border border-white/10 p-5">
                  <h2 className="text-sm font-mono font-semibold text-gray-200 uppercase tracking-wider mb-4">
                    Kit Recommendations
                  </h2>
                  <div className="grid grid-cols-2 gap-3">
                    {result.kits.map((kit, i) => (
                      <KitCard key={i} kit={kit} index={i} />
                    ))}
                  </div>
                </section>

                {/* Export PDF */}
                <button
                  onClick={handleExportPDF}
                  disabled={exporting}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white font-mono text-sm
                    uppercase tracking-wider transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="hl-export-pdf-btn"
                >
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  {exporting ? "Generating PDF..." : "Export PDF Report"}
                </button>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
